import React from 'react';
import { View, Image, ActivityIndicator, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

type Props = {
  uri: string;
  onRetry?: () => void;
  maxAttempts?: number; // default 2
  loadingTimeoutMs?: number; // default 15000
};

export const BulletinHeaderImage: React.FC<Props> = ({ uri, onRetry, maxAttempts = 2, loadingTimeoutMs = 15000 }) => {
  const styles = useThemedStyles(makeStyles);
  const { semantic } = useThemeColors();
  const [imageLoading, setImageLoading] = React.useState(true);
  const [imageError, setImageError] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const loadingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const startedRef = React.useRef(false);

  const startLoadingTimeout = React.useCallback(() => {
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      if (Platform.OS === 'web') {
        // On web, avoid remount/retry loops; fail gracefully after timeout
        setImageLoading(false);
        setImageError(true);
        return;
      }
      if (retryCount < maxAttempts) {
        setRetryCount((prev) => prev + 1);
        setImageLoading(true);
        setImageError(false);
      } else {
        setImageLoading(false);
        setImageError(true);
      }
    }, loadingTimeoutMs);
  }, [retryCount, maxAttempts, loadingTimeoutMs]);

  React.useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  const handleRetry = React.useCallback(() => {
    setRetryCount(0);
    setImageLoading(true);
    setImageError(false);
    onRetry && onRetry();
  }, [onRetry]);

  return (
    <View style={styles.imageContainer}>
      <Image
        key={Platform.OS === 'web' ? 'bulletin-image' : `bulletin-image-${retryCount}`}
        source={{ uri }}
        style={styles.headerImage}
        resizeMode="cover"
        onLoadStart={() => {
          if (!startedRef.current) {
            startedRef.current = true;
            setImageLoading(true);
            setImageError(false);
            startLoadingTimeout();
          }
        }}
        onError={() => {
          if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
          setImageLoading(false);
          setImageError(true);
        }}
        onLoad={() => {
          if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
          setImageLoading(false);
          setImageError(false);
        }}
        onLoadEnd={() => {
          if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
          if (imageLoading && !imageError) {
            setImageLoading(false);
          }
        }}
      />

      {imageLoading && (
        <View style={styles.imageLoadingOverlay}>
          <ActivityIndicator size="small" color={semantic.interactive.primary} />
          {retryCount > 0 && <Text style={styles.retryText}>{`Attempt ${retryCount + 1}`}</Text>}
        </View>
      )}

      {imageError && (
        <View style={styles.imageErrorOverlay}>
          <Text style={styles.imageErrorText}>Failed to load image</Text>
          {retryCount < maxAttempts && (
            <TouchableOpacity onPress={handleRetry} style={styles.retryButton}>
              <Text style={styles.retryButtonText}>Tap to retry</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  imageContainer: { position: 'relative', width: '100%', aspectRatio: 5/3 },
  headerImage: { width: '100%', height: '100%', borderTopLeftRadius: 14, borderTopRightRadius: 14, backgroundColor: semantic.background.tertiary },
  imageLoadingOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.8)', justifyContent: 'center', alignItems: 'center', borderTopLeftRadius: 14, borderTopRightRadius: 14 },
  imageErrorOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(240, 240, 240, 0.9)', justifyContent: 'center', alignItems: 'center', borderTopLeftRadius: 14, borderTopRightRadius: 14 },
  imageErrorText: { fontSize: 12, color: colors.text.muted, textAlign: 'center', marginBottom: 8 },
  retryText: { fontSize: 10, color: colors.text.muted, textAlign: 'center', marginTop: 4 },
  retryButton: { backgroundColor: semantic.interactive.primary, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 4, marginTop: 4 },
  retryButtonText: { fontSize: 11, color: 'white', textAlign: 'center', fontWeight: '500' },
});

export default BulletinHeaderImage;


